import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { spaService } from '../services/spaService';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

const Rides = () => {
  const [rides, setRides] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const [selectedHorse, setSelectedHorse] = useState({});
  const [savingHorse, setSavingHorse] = useState({});
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [rideToCancel, setRideToCancel] = useState(null);
  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [ridesResult, horsesResult] = await Promise.all([
        spaService.getMyRides(),
        spaService.getHorseList()
      ]);

      if (horsesResult.success) {
        setHorses(horsesResult.horses || []);
      }

      if (Array.isArray(ridesResult)) {
        setRides(ridesResult);
      } else {
        setError('Unexpected response format from server');
      }
    } catch (err) {
      console.error('Error loading rides:', err);
      setError(err.message || 'An error occurred while loading your rides');
    } finally {
      setLoading(false);
    }
  };

  const horseOptions = horses.map(horse => ({
    label: horse.horseName || 'Unnamed Horse',
    value: horse.id
  }));

  const formatDate = (dateObj) => {
    if (!dateObj) return '-';
    
    try {
      // Handle nested date structure from API
      if (dateObj.singleDate) {
        // Use the formatted dateString if available
        if (dateObj.singleDate.dateString) {
          return dateObj.singleDate.dateString;
        }
        // Fallback to dateValue if dateString is not available
        if (dateObj.singleDate.dateValue && dateObj.singleDate.dateValue !== '0001-01-01T00:00:00') {
          const date = new Date(dateObj.singleDate.dateValue);
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      }
      
      // Handle multipleDates if present
      if (dateObj.multipleDates && dateObj.multipleDates.length > 0) {
        return dateObj.multipleDates.map(d => d.dateString || formatDate(d)).join(', ');
      }
      
      // Handle simple string date format (legacy)
      if (typeof dateObj === 'string') {
        const date = new Date(dateObj);
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      
      return '-';
    } catch (err) {
      console.warn('Error formatting date:', err, dateObj);
      return '-';
    }
  };

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.date);
  };

  const eventNameBodyTemplate = (rowData) => {
    return rowData.eventName || '-';
  };

  const bibNumberBodyTemplate = (rowData) => {
    if (rowData.rideResult && (rowData.rideResult.bibNo || rowData.rideResult.bibNo === 0)) {
      return <Badge value={rowData.rideResult.bibNo} severity="info" />;
    }
    return <span className="text-400">-</span>;
  };

  const horseBodyTemplate = (rowData) => {
    let severity, value;
    
    // Check for horse in rideResult (new API structure)
    const horse = rowData.rideResult?.horse || rowData.horse;
    
    if (!horse || !horse.horseName) {
      severity = 'danger';
      value = 'No Horse Selected';
    } else {
      severity = 'success';
      value = horse.horseName;
    }
    
    return <Badge value={value} severity={severity} />;
  };

  const isWithinCancellationCutoff = (dateObj) => {
    if (!dateObj) return true; // Hide button if no date
    
    try {
      let eventDate;
      
      // Handle nested date structure from API
      if (dateObj.singleDate?.dateValue && dateObj.singleDate.dateValue !== '0001-01-01T00:00:00') {
        eventDate = new Date(dateObj.singleDate.dateValue);
      } else if (typeof dateObj === 'string') {
        eventDate = new Date(dateObj);
      } else {
        return true; // Hide button if we can't parse the date
      }
      
      // Calculate cutoff date (5 days before event at 00:00)
      const cutoffDate = new Date(eventDate);
      cutoffDate.setDate(cutoffDate.getDate() - 5);
      cutoffDate.setHours(0, 0, 0, 0);
      
      // Check if current time is past the cutoff
      const now = new Date();
      return now >= cutoffDate;
    } catch (err) {
      console.warn('Error checking cancellation cutoff:', err, dateObj);
      return true; // Hide button on error
    }
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-1">
        <Button 
          label="View Event" 
          severity="info" 
          size="small"
          onClick={() => window.open(rowData.eventUrl, '_blank')}
          icon="pi pi-external-link"
        />
      </div>
    );
  };

  const upcomingActionBodyTemplate = (rowData) => {
    const isCancelling = cancellingOrder === rowData.rideResult?.id;
    const canCancel = !isWithinCancellationCutoff(rowData.date);
    
    return (
      <div className="flex gap-1">
        <Button 
          label="View Event" 
          severity="info" 
          size="small"
          onClick={() => window.open(rowData.eventUrl, '_blank')}
          icon="pi pi-external-link"
        />
        {canCancel && (
          <Button 
            label={isCancelling ? "Cancelling..." : "Cancel"}
            severity="danger" 
            size="small"
            onClick={() => handleCancelRide(rowData)}
            icon="pi pi-times"
            disabled={isCancelling}
          />
        )}
      </div>
    );
  };

  const handleCancelRide = (ride) => {
    setRideToCancel(ride);
    setShowCancelDialog(true);
  };

  const confirmCancelRide = async () => {
    if (!rideToCancel) return;

    try {
      setCancellingOrder(rideToCancel.rideResult?.id);
      setShowCancelDialog(false);

      const result = await spaService.cancelOrder(rideToCancel.rideResult?.id);

      if (result.success) {
        // Remove the cancelled ride from the list
        setRides(prevRides => 
          prevRides.filter(r => r.rideResult?.id !== rideToCancel.rideResult?.id)
        );
        
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Ride registration cancelled successfully!',
          life: 3000
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.error || 'Failed to cancel ride registration',
          life: 3000
        });
      }
    } catch (err) {
      console.error('Error cancelling ride:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An error occurred while cancelling the ride',
        life: 3000
      });
    } finally {
      setCancellingOrder(null);
      setRideToCancel(null);
    }
  };

  const handleSaveHorse = async (ride) => {
    const selectedHorseId = selectedHorse[ride.rideResult?.id];
    
    if (!selectedHorseId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a horse',
        life: 3000
      });
      return;
    }

    try {
      setSavingHorse(prev => ({ ...prev, [ride.rideResult?.id]: true }));
      
      const horse = horses.find(h => h.id === selectedHorseId);
      
      if (!horse) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Selected horse not found',
          life: 3000
        });
        return;
      }

      // Call API to assign horse to ride result
      const assignResult = await spaService.assignHorseToResult({
        riderResultId: ride.rideResult?.id || 0,
        horseId: selectedHorseId,
        eventId: ride.eventId,
        orderLineId: ride.orderLineId
      });

      if (assignResult.success) {
        // Reload all data to get the latest state from the server
        await loadData();

        setSelectedHorse(prev => {
          const newState = { ...prev };
          delete newState[ride.rideResult?.id];
          return newState;
        });

        setExpandedRows(null);
        
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Horse selection saved successfully!',
          life: 3000
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: assignResult.error || 'Failed to save horse selection',
          life: 3000
        });
      }
    } catch (err) {
      console.error('Error saving horse:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save horse selection',
        life: 3000
      });
    } finally {
      setSavingHorse(prev => ({ ...prev, [ride.rideResult?.id]: false }));
    }
  };

  const rowExpansionTemplate = (data) => {
    // Check for horse in rideResult (new API structure) or legacy structure
    const currentHorseId = selectedHorse[data.rideResult?.id] || data.rideResult?.horse?.id || data.horse?.id || '';
    
    return (
      <div className="p-3">
        <div className="grid">
          <div className="col-12">
            <div className="se-form-group">
              <label className="se-form-label">
                Please select the horse you will be riding for this event.
                <span className="se-form-required">*</span>
              </label>
              <Dropdown
                value={currentHorseId}
                options={horseOptions}
                onChange={(e) => setSelectedHorse(prev => ({ ...prev, [data.rideResult?.id]: e.value }))}
                placeholder="Select a horse"
                className="w-full"
                disabled={horseOptions.length === 0}
              />
              {horseOptions.length === 0 && (
                <small className="text-600 mt-2 block">
                  <span className="underline cursor-pointer" onClick={() => navigate("/horse-registration")}>No horses available. Please register a horse first.</span>
                </small>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            label={savingHorse[data.rideResult?.id] ? "Saving..." : "Save"}
            icon="pi pi-check"
            className="p-button-success"
            onClick={() => handleSaveHorse(data)}
            disabled={!currentHorseId || savingHorse[data.rideResult?.id]}
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={() => setExpandedRows(null)}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading your rides..." />;
  }

  // Filter rides based on whether they have a horse selected (check both new and legacy structure)
  const upcomingRides = rides.filter(ride => {
    return !ride?.rideResult?.rideResult?.isFinalized;
  });
  
  const completedRides = rides.filter(ride => {
    return ride?.rideResult?.rideResult?.isFinalized;
  });

  return (
    <div>
      <Toast ref={toast} />
      <div className="se-card">
        <div className="se-card-header">
          <h2>My Rides</h2>
          <p className="text-600 mb-3">
            Below you will find a list of your upcoming and completed rides.
          </p>
          <p className="text-600 mb-3">
            <b>Upcoming Rides</b>: Please ensure you have selected the horse you will be using for your upcoming rides.
          </p>
          <p className="text-600 mb-3">
            <b>Completed Rides</b>: Your ride results will be ready once your SE event organiser has submitted your mileage details.
          </p>
        </div>
        <div className="se-card-content">
          
          {error && (
            <Message 
              severity="error" 
              text={error}
              className="mb-3 w-full"
            />
          )}

          <h3>Upcoming Rides</h3>
          
          {upcomingRides.length === 0 ? (
            <Message 
              severity="info" 
              text="You have no upcoming rides. Visit the Events page to register for an event."
              className="mb-3 w-full"
            />
          ) : (
            <DataTable 
              value={upcomingRides} 
              responsiveLayout="scroll" 
              className="mt-3"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="rideResult.id"
              stripedRows
            >
              <Column expander allowExpansion style={{ width: '3rem' }} />
              <Column field="eventName" header="Event Name" body={eventNameBodyTemplate} sortable />
              <Column field="date" header="Event Date" body={dateBodyTemplate} sortable />
              <Column field="location" header="Location" className="hidden-mobile" headerClassName="hidden-mobile" />
              <Column field="horse" header="Horse" body={horseBodyTemplate} />
              <Column header="Actions" body={upcomingActionBodyTemplate} />
            </DataTable>
          )}
          
          <h3 className="mt-5">Completed Rides</h3>
          
          {completedRides.length === 0 ? (
            <Message 
              severity="info" 
              text="You have no completed rides yet."
              className="mb-3 w-full"
            />
          ) : (
            <DataTable 
              value={completedRides} 
              responsiveLayout="scroll" 
              className="mt-3"
              stripedRows
              paginator={completedRides.length > 10}
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
            >
              <Column field="eventName" header="Event Name" body={eventNameBodyTemplate} sortable />
              <Column field="date" header="Event Date" body={dateBodyTemplate} sortable />
              <Column field="location" header="Location" className="hidden-mobile" headerClassName="hidden-mobile" />
              <Column field="rideResult.bibNo" header="Bib #" body={bibNumberBodyTemplate} className="hidden-mobile" headerClassName="hidden-mobile" />
              <Column field="horse" header="Horse" body={horseBodyTemplate} sortable />
              <Column header="Actions" body={actionBodyTemplate} />
            </DataTable>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        header="Cancel Ride Registration"
        visible={showCancelDialog}
        style={{ width: '450px' }}
        onHide={() => setShowCancelDialog(false)}
        footer={
          <div>
            <Button 
              label="Cancel" 
              icon="pi pi-times" 
              onClick={() => setShowCancelDialog(false)} 
              className="p-button-text"
            />
            <Button 
              label="Proceed" 
              icon="pi pi-check" 
              onClick={confirmCancelRide} 
              severity="danger"
            />
          </div>
        }
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem', color: 'var(--orange-500)' }}></i>
          <span>
            Are you sure you want to cancel your registration for this ride?
            {rideToCancel && (
              <div className="mt-3">
                <strong>Event:</strong> {rideToCancel.location}<br />
                <strong>Date:</strong> {formatDate(rideToCancel.date)}
              </div>
            )}
          </span>
        </div>
      </Dialog>
    </div>
  );
};

export default Rides;
